import { z } from 'zod';
/**
 * autodev_smoke_v1 — UI smoke scenario specification produced by the
 * AutoDev coder for each PR (Layer 3 quality gate, roadmap #166).
 *
 * The Playwright runner consumes this YAML/JSON, opens `preview_url` in
 * a headless browser, executes each scenario's steps, and emits a
 * pass/fail verdict per scenario.
 *
 * Steps are intentionally a small dialect of common Playwright actions
 * — keeping the schema tight makes it easy for the LLM to produce
 * valid output and easy for the runner to validate without false
 * positives. Anything more exotic should be expressed via a custom
 * `script` step in a follow-up schema bump.
 */
const stepGotoSchema = z.object({ goto: z.string().min(1) });
const stepTypeSchema = z.object({
    type: z.string().min(1), // selector
    text: z.string(),
});
const stepPressSchema = z.object({
    press: z.string().min(1), // selector or 'document'
    key: z.string().min(1),
});
const stepClickSchema = z.object({ click: z.string().min(1) });
const stepWaitForSelectorSchema = z.object({
    waitFor: z.string().min(1),
    timeoutMs: z.number().int().positive().optional(),
});
const stepWaitMsSchema = z.object({ wait: z.number().int().positive() });
const stepScrollToSchema = z.object({
    scrollTo: z.object({
        selector: z.string().min(1),
        offset: z.enum(['top', 'bottom']),
    }),
});
const stepAssertSchema = z.object({
    assert: z.object({
        selector: z.string().min(1),
        visible: z.boolean().optional(),
        textContains: z.string().optional(),
        countAtLeast: z.number().int().nonnegative().optional(),
        /**
         * When set to true, asserts that at least one [data-testid="csp-violation-item"]
         * element exists whose `data-csp-directive` attribute matches the given selector
         * suffix. Used by the Canvas CSP smoke scenario.
         */
        cspViolation: z.boolean().optional(),
    }),
});
const stepAssertScrollPositionSchema = z.object({
    assertScrollPosition: z.object({
        selector: z.string().min(1),
        stays: z.enum(['top', 'bottom']),
        toleranceMs: z.number().int().positive().optional(),
    }),
});
export const smokeStepSchema = z.union([
    stepGotoSchema,
    stepTypeSchema,
    stepPressSchema,
    stepClickSchema,
    stepWaitForSelectorSchema,
    stepWaitMsSchema,
    stepScrollToSchema,
    stepAssertSchema,
    stepAssertScrollPositionSchema,
]);
export const smokeScenarioSchema = z.object({
    name: z.string().min(1),
    surface: z.string().min(1),
    description: z.string().optional(),
    steps: z.array(smokeStepSchema).min(1),
    timeoutMs: z.number().int().positive().optional(),
});
export const smokeAuthSchema = z.object({
    user: z.string().email(),
    passwordEnv: z.string().min(1),
});
export const autodevSmokeV1Schema = z.object({
    schema: z.literal('autodev_smoke_v1'),
    issue: z.number().int().positive().optional(),
    preview_url: z.string().url(),
    auth: smokeAuthSchema.optional(),
    scenarios: z.array(smokeScenarioSchema).min(1),
});
//# sourceMappingURL=autodevSmoke.schema.js.map